import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ArrowLeft, Filter } from 'lucide-react';
import { Header } from '../components/Header';
import { NotificationItem } from '../components/NotificationItem';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/Button';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { NotificationType } from '../types';

export const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotificationById,
  } = useNotifications();

  const [filterType, setFilterType] = useState<'all' | 'unread' | NotificationType>('all');

  const theme = user?.role === 'provider' ? 'green' : 'blue';

  const filteredNotifications = useMemo(() => {
    if (filterType === 'all') {
      return notifications;
    }
    if (filterType === 'unread') {
      return notifications.filter(n => !n.isRead);
    }
    return notifications.filter(n => n.type === filterType);
  }, [notifications, filterType]);

  const groupedNotifications = useMemo(() => {
    const groups: { [key: string]: typeof notifications } = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: [],
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    filteredNotifications.forEach(notification => {
      const notificationDate = new Date(notification.createdAt);
      const notificationDay = new Date(
        notificationDate.getFullYear(),
        notificationDate.getMonth(),
        notificationDate.getDate()
      );

      if (notificationDay.getTime() === today.getTime()) {
        groups.today.push(notification);
      } else if (notificationDay.getTime() === yesterday.getTime()) {
        groups.yesterday.push(notification);
      } else if (notificationDate >= weekAgo) {
        groups.thisWeek.push(notification);
      } else {
        groups.older.push(notification);
      }
    });

    return groups;
  }, [filteredNotifications]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme === 'blue' ? 'from-blue-50 via-white to-blue-50' : 'from-green-50 via-white to-green-50'}`}>
      <Header theme={theme} showNotifications={false} />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className={`${theme === 'blue' ? 'bg-blue-600' : 'bg-green-600'} text-white p-6`}>
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={handleBack}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div className="flex items-center gap-3">
                <Bell className="h-8 w-8" />
                <div>
                  <h1 className="text-2xl font-bold">Notifications</h1>
                  {unreadCount > 0 && (
                    <p className="text-sm text-gray-100 mt-1">
                      You have {unreadCount} unread {unreadCount === 1 ? 'notification' : 'notifications'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as any)}
                  className="bg-white text-gray-900 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white"
                >
                  <option value="all">All Notifications</option>
                  <option value="unread">Unread Only</option>
                  <option value="BOOKING_APPROVED">Booking Approved</option>
                  <option value="BOOKING_COMPLETED">Booking Completed</option>
                  <option value="BOOKING_CANCELLED">Booking Cancelled</option>
                  <option value="BOOKING_REJECTED">Booking Rejected</option>
                  <option value="BOOKING_CREATED">New Bookings</option>
                  <option value="MESSAGE_RECEIVED">Messages</option>
                  <option value="POST_RESPONSE">Post Responses</option>
                  <option value="REVIEW_RECEIVED">Reviews</option>
                </select>
              </div>

              {unreadCount > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as read
                </Button>
              )}
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No notifications
                </h3>
                <p className="text-gray-600">
                  {filterType === 'unread'
                    ? "You're all caught up! No unread notifications."
                    : filterType === 'all'
                    ? "You don't have any notifications yet."
                    : `No ${filterType.toLowerCase().replace('_', ' ')} notifications.`}
                </p>
              </div>
            ) : (
              <>
                {Object.entries(groupedNotifications).map(([groupKey, groupNotifications]) => {
                  if (groupNotifications.length === 0) return null;

                  const groupTitles: { [key: string]: string } = {
                    today: 'Today',
                    yesterday: 'Yesterday',
                    thisWeek: 'This Week',
                    older: 'Older',
                  };

                  return (
                    <div key={groupKey}>
                      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-700">
                          {groupTitles[groupKey]}
                        </h3>
                      </div>
                      {groupNotifications.map((notification) => (
                        <NotificationItem
                          key={notification._id}
                          notification={notification}
                          onMarkAsRead={markAsRead}
                          onDelete={deleteNotificationById}
                        />
                      ))}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
